<template>
  <div v-loading="loading" element-loading-spinner="particle-loader">
    <el-alert v-if="noProjects" title="No Projects Available" type="info" center :closable="false"> </el-alert>
    <div v-if="!noProjects">
      <div class="calendarToolbar">
        <div class="calendarToolbarLeft">
          <el-select class="calendarToolbarSelect" v-model="project" placeholder="Select Project" popper-class="projectSelector" @change="handleSelectedView">
            <el-option v-for="item in codingDashboardProjects" :key="item.id" :label="item.projectID" :value="item.id">
              <div style="margin: 0px 0px 0px 0px; padding: 0px 0px 0px 0px;">{{ item.projectID }}</div>
              <div style="margin: 0px 0px 0px 0px; padding: 0px 0px 10px 0px;line-height: 0px;">
                <small>{{ `${formatDate(item.startDate)} - ${formatDate(item.endDate)}` }}</small>
              </div>
            </el-option>
          </el-select>
          <el-select class="calendarToolbarSelect" v-model="coderGrouping" placeholder="Filter by Coder" popper-class="projectSelector" clearable multiple :collapse-tags="true">
            <el-option v-for="item in compCoderFilter" :key="item.adAcct" :label="item.consultantName" :value="item.adAcct">
              <div style="margin: 0px 0px 0px 0px; padding: 0px 0px 0px 0px;">{{ item.consultantName }}</div>
              <div style="margin: 0px 0px 0px 0px; padding: 0px 0px 10px 0px;line-height: 0px;">
                <small>{{ item.consultantNumber }}</small>
              </div>
            </el-option>
          </el-select>
          <el-select class="calendarToolbarSelect" v-model="jobTitleGrouping" placeholder="Filter by Type" popper-class="projectSelector" clearable multiple :collapse-tags="true">
            <el-option v-for="item in compJobTitleFilter" :key="item.jobTitle" :label="item.jobTitle" :value="item.jobTitle"> </el-option>
          </el-select>
        </div>
        <div class="calendarToolbarRight">
          <router-link :to="`/${currentClientId}/requests`">
            <el-button style="margin-right: 8px;"> <i style="font-size: 14px;" class="mdi mdi-clipboard-text-outline"></i> Manage Schedule Requests </el-button>
          </router-link>
          <el-button @click="viewSchedule()"> <i style="font-size: 14px;" class="mdi mdi-calendar-account"></i> View PDF Schedule </el-button>
        </div>
      </div>
      <v-calendar ref="customCalendar" is-expanded :attributes="compCalendarData" :key="calendarMonthKey" class="custom_calendar" :min-date="compMinDate" :max-date="compMaxDate">
        <div ref="daySlot" slot="day-content" slot-scope="{ day, attributes }" class="day-content">
          <div class="day-cell">
            <div style="margin: 0px 0px 0px 2px; color: #1c3c5f;" class="day-label">{{ day.label }}</div>
            <div class="day-contents">
              <div v-if="attributes && attributes.length">
                <el-popover placement="top" :title="'Hourly Totals'" trigger="click" width="400">
                  <el-table class="hoursTable" :data="getHourlyTotals(filterByGrouping(attributes[0].customData), day.week)" :stripe="true" size="mini" :fit="true">
                    <el-table-column prop="item" label="Item" sortable></el-table-column>
                    <el-table-column prop="total" label="Total" sortable></el-table-column>
                  </el-table>
                  <i slot="reference" style="float: right; margin: -23px 2px 0px 0px;font-size: 18px; cursor: pointer; color: #f90089;" title="Hourly Totals" class="mdi mdi-sigma"></i>
                </el-popover>
                <table class="calendarConsultantTable">
                  <colgroup>
                    <col class="calendarConsultantNameCol" />
                    <col class="calendarConsultantHoursCol" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in filterByGrouping(attributes[0].customData)" :key="`${day.day}_${idx}`">
                      <td class="calendarConsultantName">
                        <div class="calendarConsultantNameText" :title="row && row.consultantName ? row.consultantName : ''" :style="row && row.__placeholder ? 'cursor: default; opacity: 0.35;' : 'cursor: pointer; color: #303133;'" :class="`consultantInfo_${day.day}_${idx}`" @click="!(row && row.__placeholder) && showConsultantPopOver({ row: row, $index: idx }, day, $event)">
                          <span class="calendarConsultantNameLabel">{{ row.consultantName }}</span>
                          <i v-if="row && row.comments" class="mdi mdi-comment-text-outline calendarConsultantCommentIcon" title="Has notes"></i>
                        </div>
                      </td>
                      <td class="calendarConsultantHours">{{ row.hoursWorked }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </v-calendar>
      <el-popover ref="hourlyTotals" placement="top" :title="'Hourly Totals'" trigger="manual" width="400">
        <el-table class="hoursTable" :data="hourlyPopOverData.data" :stripe="true" size="mini" :fit="true">
          <el-table-column prop="item" label="Item" sortable></el-table-column>
          <el-table-column prop="total" label="Total" sortable></el-table-column>
        </el-table>
      </el-popover>
      <el-popover ref="consultantInfo" popper-class="customCalendarConsultantInfoPopover" placement="top" :title="`${consultantPopOverData.consultantName} - ${consultantPopOverData.month}/${consultantPopOverData.day}/${consultantPopOverData.year}`" trigger="manual" :key="consultantPopOverData.consultantNumber" width="900">
        <el-table style="height: auto;" :data="[consultantPopOverData.row]" :fit="true">
          <el-table-column prop="consultantName" label="" width="40">
            <template slot-scope="scope">
              <avatar :fullname="scope.row.consultantName" :image="scope.row.photo" :size="30"> </avatar>
            </template>
          </el-table-column>
          <el-table-column prop="consultantNumber" label="Name"> </el-table-column>
          <el-table-column prop="startTimeFormat" label="Start Time"> </el-table-column>
          <el-table-column prop="lunchOutFormat" label="Lunch Out"> </el-table-column>
          <el-table-column prop="lunchInFormat" label="Lunch In"> </el-table-column>
          <el-table-column prop="outTimeFormat" label="Out Time"> </el-table-column>
          <el-table-column prop="consTimeZone" label="Time Zone"> </el-table-column>
          <el-table-column prop="hoursWorked" label="Sched. Hours"> </el-table-column>
          <el-table-column prop="comments" label="Notes">
            <template slot-scope="scope">
              <div style="white-space: normal; word-break: normal; overflow-wrap: normal; line-height: 1.4;">{{ scope.row.comments }}</div>
            </template>
          </el-table-column>
        </el-table>
      </el-popover>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { format, parseISO } from 'date-fns'
import { setupCalendar, Calendar } from 'v-calendar'
import 'v-calendar/lib/v-calendar.min.css'
import avatar from '@/components/avatar'

setupCalendar()

export default {
  name: 'CodingCalendarSchedule',
  // mixins: [HiaAuthorization, money, hiaFilters],
  props: {
    initialProjectId: { type: [String, Number], default: null }
  },
  components: {
    avatar,
    'v-calendar': Calendar
  },
  data: function() {
    return {
      loading: false,
      jobTitleGrouping: [],
      coderGrouping: [],
      noProjects: false,
      project: '',
      projectStart: new Date(),
      projectEnd: new Date(),
      calendarViewToggle: false,
      currentProject: '',
      consultantPopOverData: {
        consultantName: null,
        month: null,
        day: null,
        year: null,
        consultantNumber: null,
        row: []
      },
      hourlyPopOverData: {
        data: null,
        week: null
      },
      coders: []
      // isOpen: false
    }
  },
  methods: {
    ...mapActions('dashboard/', ['GET_DASHBOARD_PROJECTS_CODING', 'GET_CALENDAR_SCHEDULE', 'GET_SCHEDULE_PDF_URL']),
    ...mapMutations('dashboard/', ['CLEAR_CALEDNAR_SCHEDULE']),
    initialize() {
      this.noProjects = false
      this.calendarViewToggle = false
      this.loading = true
      let param = {
        startDate: this.codingDateRange[0],
        endDate: this.codingDateRange[1],
        initialProjectId: this.initialProjectId
      }
      this.GET_DASHBOARD_PROJECTS_CODING(param).then(response => {
        this.loading = false
        if (this.codingDashboardProjects.length) {
          const selected = this.codingDashboardProjects.find(p => Number(p.id) === this.initialProjectId) || this.codingDashboardProjects[0]
          this.project = selected.id
          this.handleView(selected)
        } else {
          this.noProjects = true
        }
      })
    },
    handleView(row) {
      this.projectStart = row.startDate
      this.projectEnd = row.endDate
      const loading = this.$loading({
        lock: true,
        text: 'generating Schedule...',
        spinner: 'nucleus-loader',
        background: 'rgba(255, 255, 255, 0.7)'
      })
      this.$nextTick(() => {
        this.GET_CALENDAR_SCHEDULE({ projectId: row.id }).then(
          response => {
            loading.close()
            this.$nextTick(() => {
              this.currentProject = row.projectID
              this.calendarViewToggle = true
            })
          },
          error => {
            loading.close()
            console.log(error)
          }
        )
      })
    },
    formatDate(date) {
      return format(parseISO(date), 'MM/dd/yyyy')
    },
    formatTime(dateTime) {
      return format(parseISO(dateTime), 'hh:mm a')
    },
    handleSelectedView(val) {
      let project = this.codingDashboardProjects.filter(f => f.id === val)
      if (project.length) {
        this.coderGrouping = []
        this.jobTitleGrouping = []
        this.handleView(project[0])
      }
    },
    viewSchedule() {
      let project = this.codingDashboardProjects.filter(f => f.id === this.project)
      if (project.length) {
        this.handleScheduleView(project[0].id)
      }
    },
    filterByGrouping(data) {
      let newData = data

      if (this.coderGrouping.length) {
        newData = newData.filter(f => this.coderGrouping.find(x => x === f.adAcct))
      }

      if (this.jobTitleGrouping.length) {
        newData = newData.filter(f => this.jobTitleGrouping.find(x => x === f.jobTitle))
      }

      return newData
    },
    getDailyTotal(data) {
      let dailyHours = data.reduce((total, currentValue) => {
        if (currentValue) {
          return currentValue.hoursWorked !== null ? total + currentValue.hoursWorked : 0
        }
      }, 0)
      return dailyHours
    },
    getWeeklyMonthlyTotal(week) {
      let weeks = [1, 2, 3, 4, 5, 6, 7]
      if (week) {
        weeks = [week]
      }
      let monthTotal = 0
      if (this.$refs.customCalendar.$children[0].$children.find(f => f.name === 'weeks-slide-right' || f.name === 'weeks-slide-left')) {
        let allDays = this.$refs.customCalendar.$children[0].$children
          .find(f => f.name === 'weeks-slide-right' || f.name === 'weeks-slide-left')
          .$children[0].$children.filter((v, i, arr) => {
            return arr.findIndex(f => f.day.day === v.day.day) === i
          })
        let monthData = allDays.filter(f => weeks.find(n => n === f.day.week))
        monthData.forEach((v, i) => {
          if (v.attributesList[0]) {
            monthTotal += this.getDailyTotal(this.filterByGrouping(v.attributesList[0].customData))
          }
        })
      }
      return monthTotal
    },
    getHourlyTotals(data, week) {
      let array = []
      this.$nextTick(() => {
        if (data) {
          let daily = {
            item: 'Daily',
            total: this.getDailyTotal(data)
          }
          let weekly = {
            item: `Week ${week}`,
            total: this.getWeeklyMonthlyTotal(week)
          }
          let monthly = {
            item: `Monthly`,
            total: this.getWeeklyMonthlyTotal(null)
          }

          array.push(daily, weekly, monthly)
        }
      })
      return array
    },
    handleScheduleView(projectId) {
      const loading = this.$loading({
        lock: true,
        text: 'Getting ready...',
        spinner: 'nucleus-loader',
        background: 'rgba(255, 255, 255, 0.7)'
      })
      this.GET_SCHEDULE_PDF_URL({ projectId: projectId }).then(response => {
        loading.close()
        window.open(`/static/pdfjs/web/viewer.html?file=${response}`, '_blank')
      })
    },
    showConsultantPopOver(scope, day, event) {
      this.consultantPopOverData = {
        consultantName: scope.row.consultantName,
        month: day.month,
        day: day.day,
        year: day.year,
        consultantNumber: scope.row.consultantNumber,
        row: scope.row
      }

      this.$nextTick(() => {
        let popper = this.$refs.consultantInfo
        popper.doDestroy()
        let reference = event.target
        popper.referenceElm = reference
        popper.doShow()
        this.handleHidePopOver(popper)
      })
    },
    showHourlyTotalPopOver(data, day) {
      this.$nextTick(() => {
        this.hourlyPopOverData = {
          data: data,
          week: day.week
        }
        let popper = this.$refs.hourlyTotals
        popper.doDestroy()
        let reference = document.querySelector(`.hourlyTotals_${day.day}`)
        popper.referenceElm = reference
        popper.doShow()
        this.handleHidePopOver(popper)
      })
    },
    handleHidePopOver(popper) {
      let handler = e => {
        if (popper.$refs.popper) {
          if (!popper.$refs.popper.contains(e.target)) {
            this.$nextTick(() => {
              popper.doClose()
              popper.doDestroy()
              document.removeEventListener('click', handler, false)
            })
          }
        }
      }
      document.addEventListener('click', handler, false)
    }
  },
  created: function() {
    this.initialize()
  },
  computed: {
    ...mapState('user/', ['currentClient', 'currentClientId']),
    ...mapState('dashboard/', ['codingDashboardProjects', 'schedule', 'calendarSchedule', 'codingDateRange']),
    compMinDate() {
      return new Date(this.projectStart)
    },
    compMaxDate() {
      return new Date(this.projectEnd)
    },
    calendarMonthKey() {
      const d = this.projectStart ? new Date(this.projectStart) : new Date()
      const year = d.getFullYear()
      const month = d.getMonth() + 1
      return `${year}-${String(month).padStart(2, '0')}`
    },
    compCalendarData() {
      const schedule = Array.isArray(this.calendarSchedule) ? this.calendarSchedule : []
      // Build a stable, distinct list of consultants across the whole range.
      // Use consultantNumber as the primary identity key.
      const consultantMap = new Map()
      schedule.forEach(day => {
        const list = Array.isArray(day && day.customData) ? day.customData : []
        list.forEach(row => {
          if (!row) return
          const key = row.consultantNumber || row.adAcct || row.consultantName
          if (!key) return
          if (!consultantMap.has(key)) {
            consultantMap.set(key, {
              consultantNumber: row.consultantNumber || '',
              adAcct: row.adAcct || '',
              consultantName: row.consultantName || '',
              jobTitle: row.jobTitle || '',
              photo: row.photo || '',
              consTimeZone: row.consTimeZone || '',
              timeZone: row.timeZone || ''
            })
          }
        })
      })

      const consultants = Array.from(consultantMap.values()).sort((a, b) => {
        const nameA = (a.consultantName || '').toLowerCase()
        const nameB = (b.consultantName || '').toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        const numA = (a.consultantNumber || '').toLowerCase()
        const numB = (b.consultantNumber || '').toLowerCase()
        if (numA < numB) return -1
        if (numA > numB) return 1
        return 0
      })

      // Pad each day so every consultant is always at the same index.
      return schedule.map(day => {
        const dayList = Array.isArray(day && day.customData) ? day.customData : []
        const byKey = new Map()
        dayList.forEach(row => {
          if (!row) return
          const key = row.consultantNumber || row.adAcct || row.consultantName
          if (key) byKey.set(key, row)
        })

        const padded = consultants.map(c => {
          const key = c.consultantNumber || c.adAcct || c.consultantName
          const existing = byKey.get(key)
          if (existing) return existing

          return {
            ...c,
            __placeholder: true,
            scheduleDate: (day && (day.dates || day.key)) || null,
            comments: null,
            startTime: null,
            lunchOut: null,
            lunchIn: null,
            outTime: null,
            hoursWorked: null,
            startTimeFormat: '',
            lunchOutFormat: '',
            lunchInFormat: '',
            outTimeFormat: ''
          }
        })

        return {
          ...day,
          customData: padded
        }
      })
    },
    compCoderFilter() {
      let data = [].concat
        .apply([], this.calendarSchedule.map(m => m.customData))
        .map(m => {
          let item = {
            adAcct: m.adAcct,
            consultantName: m.consultantName,
            consultantNumber: m.consultantNumber,
            photo: m.photo
          }
          return item
        })
        .filter((v, i, arr) => {
          return arr.findIndex(f => f.adAcct === v.adAcct) === i
        })

      return data
    },
    compJobTitleFilter() {
      let data = [].concat
        .apply([], this.calendarSchedule.map(m => m.customData))
        .map(m => {
          if (m.jobTitle) {
            let item = {
              jobTitle: m.jobTitle
            }
            return item
          }
        })
        .filter((v, i, arr) => {
          return arr.findIndex(f => f.jobTitle === v.jobTitle) === i
        })

      return data
    }
  },
  watch: {
    codingDateRange() {
      this.initialize()
    },
    initialProjectId() {
      const match = this.getProjectFromInitialProjectId()
      if (!match) return
      if (match && String(this.project) !== String(match.id)) {
        this.project = match.id
        this.handleView(match)
      }
    }
  }
}
</script>

<style scoped>
/* project selector */
.calendarToolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0 0 10px 0;
  text-align: left;
  font-weight: 400;
}

.calendarToolbarLeft {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 720px;
}

.calendarToolbarRight {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}

.calendarToolbarSelect {
  width: 300px;
}

.projectSelector .el-select-dropdown__list .el-select-dropdown__item {
  height: 100% !important;
}

/* v-calendar */
.custom_calendar >>> .c-weekdays {
  border-top: 1px solid #b8c2cc;
  border-bottom: 1px solid #b8c2cc;
  background: #f8fafc;
}

.custom_calendar >>> .c-weeks {
  background: #ffffff;
  padding: 0px 0px 0px 0px;
}

.custom_calendar >>> .c-weeks .c-weeks-rows-wrapper .c-day-popover {
  /* display: flex;
    align-items: flex-start; */
  min-height: 90px;
}

.custom_calendar >>> .c-weeks .c-weeks-rows-wrapper .c-week .c-day {
  border-right: 1px solid #b8c2cc;
  border-bottom: 1px solid #b8c2cc;
  background: #ffffff;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.custom_calendar >>> .day-content {
  width: 100%;
}

.custom_calendar >>> .consultantRow {
  cursor: pointer;
  margin: 0px 0px 5px 0px;
}

.custom_calendar >>> .consultantRow:hover {
  background: #b8c2cc;
}

.custom_calendar >>> .consultantRow .avatarCol {
  height: 30px;
}

.custom_calendar >>> .c-weeks .c-weeks-rows-wrapper .c-week .c-day .c-day-content-wrapper {
  justify-content: unset;
}

.custom_calendar >>> .c-weeks .c-weeks-rows-wrapper .c-week .c-day-popover:first-child .c-day {
  border-left: 0px solid #b8c2cc;
  background: #eff8ff;
}

.custom_calendar >>> .c-weeks .c-weeks-rows-wrapper .c-week .c-day-popover:last-child .c-day {
  border-right: 0px solid #b8c2cc;
  background: #eff8ff;
}

/* calendar consultant table */
.custom_calendar >>> .caret-wrapper,
.hoursTable >>> .caret-wrapper {
  display: none;
}

.custom_calendar >>> .el-table__header thead tr {
  background: transparent;
}

.custom_calendar >>> .el-table__header thead tr th {
  background: transparent;
}

.custom_calendar >>> .el-table {
  background-color: transparent;
}

.custom_calendar >>> .el-table .cell {
  white-space: nowrap !important;
}

.custom_calendar .calendarConsultantTable {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 4px; /* keep a little header breathing room */
}

.custom_calendar .calendarConsultantTable thead th {
  height: 18px;
  padding: 0;
  background: transparent;
  border: 0;
}

.custom_calendar .calendarConsultantTable tbody tr td {
  border-top: 1px solid #ebeef5;
  padding: 8px 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom_calendar .calendarConsultantTable tbody tr:nth-child(odd) td {
  background: #ffffff;
}

.custom_calendar .calendarConsultantTable tbody tr:nth-child(even) td {
  background: #fafafa;
}

.custom_calendar .calendarConsultantTable tbody tr:hover td {
  background: #f0f7ff;
}

.custom_calendar .calendarConsultantTable .calendarConsultantHours {
  width: 32px;
  text-align: right;
  padding-right: 4px;
}

.custom_calendar .calendarConsultantTable .calendarConsultantName {
  width: calc(100% - 32px);
}

.custom_calendar .calendarConsultantTable .calendarConsultantNameText {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  overflow: hidden;
}

.custom_calendar .calendarConsultantTable .calendarConsultantNameLabel {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom_calendar .calendarConsultantTable .calendarConsultantCommentIcon {
  flex: 0 0 auto;
  font-size: 12px;
  color: #909399;
}

.custom_calendar .calendarConsultantTable .calendarConsultantNameCol {
  width: calc(100% - 32px);
}

.custom_calendar .calendarConsultantTable .calendarConsultantHoursCol {
  width: 32px;
}
</style>

<style>
.customCalendarConsultantInfoPopover {
  transform: translateY(25px) !important;
}
</style>
